export type ConsentChoice = "granted" | "denied";

const STORAGE_KEY = "ajility-consent-analytics";
const OPEN_EVENT = "ajility:open-consent-settings";

/**
 * Shared first-party cookie. The Operating Scan application on
 * diagnostic.ajility.com.au reads and writes the same cookie, so a visitor is
 * only ever asked once across both. Preview hosts cannot set a cookie on the
 * production domain, so they fall back to local storage only.
 */
const COOKIE_NAME = "ajility_consent";
const COOKIE_DOMAIN = ".ajility.com.au";
const COOKIE_MAX_AGE = 180 * 24 * 60 * 60;

type Listener = (choice: ConsentChoice | null) => void;

const listeners = new Set<Listener>();
let current: ConsentChoice | null = null;
let hydrated = false;

function onRootDomain(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "ajility.com.au" || host.endsWith(".ajility.com.au");
}

function readCookie(): ConsentChoice | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"),
  );
  if (!match) return null;
  const value = decodeURIComponent(match[1] ?? "");
  if (value === "accepted") return "granted";
  if (value === "declined") return "denied";
  return null;
}

function writeCookie(choice: ConsentChoice | null): void {
  if (!onRootDomain()) return;
  const base = `${COOKIE_NAME}=`;
  const attrs = `; domain=${COOKIE_DOMAIN}; path=/; SameSite=Lax; Secure`;
  if (choice === null) {
    document.cookie = `${base}${attrs}; Max-Age=0`;
    return;
  }
  const value = choice === "granted" ? "accepted" : "declined";
  document.cookie = `${base}${value}${attrs}; Max-Age=${COOKIE_MAX_AGE}`;
}

function readStored(): ConsentChoice | null {
  const shared = readCookie();
  if (shared !== null) return shared;
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

/** Reads the stored choice once per page load. Safe to call repeatedly. */
export function hydrateConsent(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  if (!hydrated) {
    hydrated = true;
    current = readStored();
    if (current !== null) notify();
  }
  return current;
}

function notify() {
  listeners.forEach((listener) => listener(current));
}

export function getConsent(): ConsentChoice | null {
  return current;
}

export function setConsent(choice: ConsentChoice): void {
  current = choice;
  try {
    window.localStorage.setItem(STORAGE_KEY, choice);
  } catch {
    // Storage unavailable: the choice still applies for this page view.
  }
  writeCookie(choice);
  notify();
}

/** Clears the stored choice so the consent banner is shown again. */
export function clearConsent(): void {
  current = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the banner still reopens for this page view.
  }
  writeCookie(null);
  notify();
}

export function subscribeConsent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Called by the footer link so a visitor can change their mind. */
export function openConsentSettings(): void {
  if (typeof window === "undefined") return;
  clearConsent();
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function subscribeConsentSettingsOpen(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}
